import { Badge, CircularProgress, Button as MUIButton, styled, ThemeProvider, Typography } from '@mui/material';
import { forwardRef } from 'react';
import FlatIcon from '../../../ui/FlatIcon';
import { createSeencTheme } from 'src/theme/seenc';

const StyledButton = styled(MUIButton)(({ theme, variant }) => ({
  textTransform: 'none',
  padding: '6px 20px',
  gap: '8px',
  minHeight: variant === 'outlined' ? '46px' : '44px',
  maxHeight: variant === 'outlined' ? '46px' : '44px',
  position: 'relative',
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '2px',
  },
  transition: theme.transitions.create(['background-color', 'box-shadow', 'border-color', 'color'], {
    duration: theme.transitions.duration.short,
  }),
}));

const LoadingOverlay = styled('div')({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const SeencLoginButton = forwardRef(function SeencLoginButton(
  {
    fullWidth = true,
    type = 'button',
    disabled = false,
    color = 'primary',
    variant = 'contained',
    loading = false,
    loadingText,
    sx = {},

    icon,
    iconPosition = 'start',
    iconProps = {},

    children,

    badgeContent,

    ...otherProps
  },
  ref
) {
  const isDisabled = disabled || loading;
  const seencTheme = createSeencTheme();

  const buttonContent = (
    <ThemeProvider theme={seencTheme}>
      <StyledButton
        ref={ref}
        fullWidth={fullWidth}
        color={color}
        variant={variant}
        type={type}
        disabled={isDisabled}
        sx={sx}
        aria-busy={loading}
        {...otherProps}>
        <Typography
          variant='seenc_button'
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1,
            opacity: loading ? 0.3 : 1,
            transition: 'opacity 0.2s ease-in-out',
            whiteSpace: 'nowrap',
          }}>
          {icon && iconPosition === 'start' && (
            <FlatIcon
              icon={icon}
              aria-hidden='true'
              style={{ fontSize: '1rem', flexShrink: 0 }}
              {...iconProps}
            />
          )}

          {loading && loadingText ? loadingText : children}

          {icon && iconPosition === 'end' && (
            <FlatIcon
              icon={icon}
              aria-hidden='true'
              style={{ fontSize: '1rem', flexShrink: 0 }}
              {...iconProps}
            />
          )}
        </Typography>

        {loading && (
          <LoadingOverlay>
            <CircularProgress
              size={20}
              color='primary'
              aria-label='Carregando...'
            />
          </LoadingOverlay>
        )}
      </StyledButton>
    </ThemeProvider>
  );

  return badgeContent ?
      <Badge
        badgeContent={badgeContent}
        color='primary'
        sx={{
          '& .MuiBadge-badge': {
            top: -7,
            right: -7,
            transform: 'none',
            position: 'absolute',
          },
          position: 'relative',
        }}>
        {buttonContent}
      </Badge>
    : buttonContent;
});

export default SeencLoginButton;
